import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { alertError } from "redux/reducers/master";
import { downloadBlobFile, getStaticFile } from "utils/file-util";

export const LAZY_LOAD_PDF_SCROLL_END_OPTIONS = { once: true };

function useLazyLoadForceReadPdf({
  pdfUrl,
  pdfFile,
  downloadInfo,
  enabled = true,
  onReachEnd: onReachEndCallback,
} = {}) {
  const dispatch = useDispatch();
  const pdfDownloadInfo = useRef();

  const [pdf, setPDF] = useState(null);
  const [reachEnd, setReachEnd] = useState(false);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [initialWindowReady, setInitialWindowReady] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(false);

  const resetViewerState = useCallback(() => {
    setReachEnd(false);
    setPdfLoaded(false);
    setInitialWindowReady(false);
    setPDF(null);
    pdfDownloadInfo.current = null;
  }, []);

  // Pre-fetched File (e.g. API-generated PDF). Takes precedence over pdfUrl.
  useEffect(() => {
    if (!enabled || !pdfFile) {
      return undefined;
    }

    setLoadingPDF(false);
    setReachEnd(false);
    setPdfLoaded(false);
    setInitialWindowReady(false);
    setPDF(pdfFile);
    pdfDownloadInfo.current = downloadInfo ?? null;

    return undefined;
  }, [enabled, pdfFile, downloadInfo]);

  useEffect(() => {
    if (!enabled || !pdfUrl || pdfFile) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoadingPDF(true);
        resetViewerState();

        const { file, downloadInfo: fetchedDownloadInfo } = await getStaticFile(pdfUrl);

        if (cancelled) return;

        setPDF(file);
        pdfDownloadInfo.current = fetchedDownloadInfo;
      } catch (error) {
        if (cancelled) return;

        console.error(error);
        dispatch(alertError(error));
      } finally {
        if (!cancelled) {
          setLoadingPDF(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, enabled, pdfUrl, pdfFile, resetViewerState]);

  const onDocumentLoaded = useCallback(() => {
    setPdfLoaded(true);
  }, []);

  const onInitialPagesLoaded = useCallback(() => {
    setInitialWindowReady(true);
  }, []);

  const onReachEnd = useCallback(() => {
    setReachEnd(true);
    onReachEndCallback?.();
  }, [onReachEndCallback]);

  const onDownloadPDF = useCallback(() => {
    if (!pdfDownloadInfo.current) return;

    downloadBlobFile(...pdfDownloadInfo.current);
  }, []);

  const isViewerSpinning = loadingPDF || (pdf && !initialWindowReady);
  const downloadable = initialWindowReady && !!pdfDownloadInfo.current;

  return {
    pdf,
    reachEnd,
    pdfLoaded,
    initialWindowReady,
    loadingPDF,
    isViewerSpinning,
    downloadable,
    onDocumentLoaded,
    onInitialPagesLoaded,
    onReachEnd,
    onDownloadPDF,
    scrollEndOptions: LAZY_LOAD_PDF_SCROLL_END_OPTIONS,
  };
}

export default useLazyLoadForceReadPdf;
